import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Star } from "lucide-react";
import { ASSETS } from "../../data/site";
import { PhoneFrame } from "./PhoneFrame";
import { StoreBadges } from "./StoreBadges";
import { AppStoreQR } from "./AppStoreQR";
import { useSignup } from "../SignupProvider";

export const Hero = () => {
  const { openSignup } = useSignup();

  return (
    <section id="top" className="relative overflow-hidden pt-32 md:pt-40">
      <div className="ss-grain pointer-events-none absolute inset-0 opacity-70" />
      <div
        className="pointer-events-none absolute -top-40 right-0 h-[520px] w-[520px] rounded-full opacity-50 blur-3xl"
        style={{ background: "radial-gradient(circle, #E4EDE6 0%, transparent 70%)" }}
      />

      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 pb-24 md:grid-cols-2 md:gap-10 md:px-12 md:pb-32">
        {/* Left */}
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-[#2F5E43]/15 bg-white/70 px-4 py-1.5 text-xs font-medium text-[#2F5E43] backdrop-blur"
            data-testid="hero-badge"
          >
            <span className="flex h-1.5 w-1.5 rounded-full bg-[#2F5E43]" />
            Built with women in mind
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-heading mt-6 text-5xl font-semibold leading-[1.05] tracking-tight text-[#111E16] md:text-6xl lg:text-7xl"
            data-testid="hero-headline"
          >
            Your Safety
            <br />
            Network <span className="text-[#2F5E43]">Starts Here.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 max-w-xl text-lg leading-relaxed text-[#4A5F52]"
            data-testid="hero-subheadline"
          >
            Built with women in mind, SisterSafe helps you stay connected to the people
            you trust while travelling, commuting, dating, shopping, or simply getting
            home safely.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-9 flex flex-col gap-3 sm:flex-row"
          >
            <button
              onClick={() => openSignup("waitlist")}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#2F5E43] px-8 py-4 font-medium text-white transition-colors hover:bg-[#244A34]"
              data-testid="hero-waitlist-btn"
            >
              Join the Waitlist
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <button
              onClick={() => openSignup("beta")}
              className="inline-flex items-center justify-center rounded-full bg-[#E4EDE6] px-8 py-4 font-medium text-[#2F5E43] transition-colors hover:bg-[#D5E2D8]"
              data-testid="hero-beta-btn"
            >
              Become a Beta Tester
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.35 }}
            className="mt-6 flex flex-wrap items-center gap-5"
          >
            <StoreBadges testPrefix="hero" />
            <AppStoreQR size={56} testPrefix="hero" display="hidden lg:flex" caption={false} />
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.45 }}
            className="mt-10 flex items-center gap-6 text-sm text-[#4A5F52]"
          >
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-[#2F5E43]" /> Strong security
            </div>
            <div className="flex items-center gap-2">
              <Star className="h-4 w-4 text-[#2F5E43]" /> Loved by beta testers
            </div>
          </motion.div>
        </div>

        {/* Right - phone mockups */}
        <div className="relative mx-auto flex w-full max-w-md items-end justify-center md:max-w-none md:justify-start">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-20"
          >
            <PhoneFrame
              src={ASSETS.appHome}
              alt="SisterSafe home screen with SOS and quick actions"
              testId="hero-app-mockup"
              className="w-[220px] sm:w-[248px] lg:w-[268px]"
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="relative z-30 -ml-8 mb-8 hidden sm:block"
          >
            <PhoneFrame
              src={ASSETS.safeJourney}
              alt="SisterSafe Safe Journey completed screen"
              testId="hero-secondary-mockup"
              buttons={false}
              className="w-[136px] lg:w-[152px]"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
